import { CSSProperties, useCallback, useMemo } from 'react';
import { css } from '@emotion/react';
import { theme } from '@frontend/theme';
import { CALENDAR_Z_INDICES, MIN_COLUMN_WIDTH, TOTAL_HOURS } from '../constants';
import { useScrollContainerRef } from '../hooks/use-scroll-container-ref';
import { useCalendarGridStore } from '../store/calendar-grid.store';
import { CalendarGridCreateEvent, CalendarNavigationConfig } from '../types';
import { getHoverMinutesFromEvent } from '../utils';
import { CalendarGridProvider, CalendarGridProviderProps } from './calendar-grid-provider';
import { CalendarTooltip, useCalendarTooltip } from './calendar-tooltip';
import { ColumnGroupSections } from './column-group-sections';
import { DragCreateSelectionOverlay } from './drag-create-selection-overlay';
import { useDragCreateSelectionOverlay } from './drag-create-selection-overlay/use-drag-create-selection-overlay';
import { TimeColumn } from './time-column';
import { TopCornerCells } from './top-corner-cells';

type NoEventsComponentProps = {
  maxWidth: string;
};

interface CalendarGridComponentProps {
  NoEventsComponent?: React.ComponentType<NoEventsComponentProps>;
  hideVerticalOverflow?: boolean;
  navigationConfig?: CalendarNavigationConfig;
  onEventCreate?: (selection: CalendarGridCreateEvent) => void;
}

const CalendarGridComponent = ({
  NoEventsComponent,
  hideVerticalOverflow,
  navigationConfig,
  onEventCreate,
}: CalendarGridComponentProps) => {
  const { containerRef, handleScroll, visibleHeight, visibleWidth } = useScrollContainerRef();
  const tooltipInfo = useCalendarTooltip(containerRef);

  const { pxPerMinute, groupIds, groupsById, columnBodyVariant, headerType } = useCalendarGridStore([
    'pxPerMinute',
    'groupIds',
    'groupsById',
    'columnBodyVariant',
    'headerType',
  ]);

  const isGridBody = columnBodyVariant === 'grid';
  const isAllowDragCreateSelection = isGridBody && !!onEventCreate;
  const hideTooltip = tooltipInfo.hideTooltip;
  const handleTooltipMouseMove = tooltipInfo.onMouseMove;

  const {
    startDragCreateSelection,
    progressDragCreateSelection,
    endDragCreateSelection,
    isDragCreateSelectionStarted,
  } = useDragCreateSelectionOverlay({
    isAllowDragCreateSelection,
    onEventCreate,
  });

  const handleMouseMove = useCallback(
    (e: React.MouseEvent<HTMLElement>) => {
      if (!containerRef.current) return;
      const targetElement = e.target instanceof HTMLElement ? e.target : null;
      const columnElement = targetElement?.closest('[data-column-id]') as HTMLElement | null;
      const hoveredEventElement = targetElement?.closest('[data-event-id]') as HTMLElement | null;
      const shouldHideTooltip = !isGridBody || isDragCreateSelectionStarted || !columnElement;

      if (shouldHideTooltip) {
        hideTooltip();
      } else {
        handleTooltipMouseMove(e.clientX, e.clientY, hoveredEventElement?.dataset.eventId);
      }

      if (isDragCreateSelectionStarted) {
        const totalMinutes = getHoverMinutesFromEvent(e.clientY, containerRef.current, pxPerMinute);
        progressDragCreateSelection(totalMinutes);
        return;
      }
    },
    [
      containerRef,
      isGridBody,
      isDragCreateSelectionStarted,
      hideTooltip,
      handleTooltipMouseMove,
      pxPerMinute,
      progressDragCreateSelection,
    ]
  );

  const handleMouseDown = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (!containerRef.current || !isAllowDragCreateSelection) return;

      const targetElement =
        e.target instanceof HTMLElement ? (e.target.closest('[data-column-id]') as HTMLElement | null) : null;
      const { columnId, groupId } = targetElement?.dataset ?? {};

      const totalMinutes = getHoverMinutesFromEvent(e.clientY, containerRef.current, pxPerMinute);
      startDragCreateSelection(columnId, groupId, totalMinutes);
      hideTooltip();
    },
    [containerRef, hideTooltip, isAllowDragCreateSelection, pxPerMinute, startDragCreateSelection]
  );

  const referenceProps = tooltipInfo.getReferenceProps({
    onMouseMove: handleMouseMove,
    onMouseUp: endDragCreateSelection,
    onMouseLeave: hideTooltip,
    onMouseDown: handleMouseDown,
  });

  const gridTemplateColumns = useMemo(() => {
    return groupIds.reduce((acc, groupId) => {
      const columnCount = groupsById[groupId]?.columnIds.length ?? 0;
      if (columnCount === 0) return acc;
      return `${acc} repeat(${columnCount}, minmax(var(--column-min-width), 1fr)) var(--group-separator-width)`;
    }, 'var(--time-column-width) ');
  }, [groupsById, groupIds]);

  const gridStyle = {
    gridTemplateColumns,
    '--row-height': `${pxPerMinute}px`,
    '--viewport-height': `${visibleHeight}px`,
    '--viewport-width': `${visibleWidth}px`,
    '--column-min-width': `${MIN_COLUMN_WIDTH[columnBodyVariant]}px`,
    ...(hideVerticalOverflow ? { overflowY: 'hidden' } : {}),
    gridTemplateRows:
      columnBodyVariant === 'stacked'
        ? `var(--header-height) auto`
        : `var(--header-height) repeat(var(--total-mins), var(--row-height))`,
    backgroundColor: headerType === 'weekday' ? theme.colors.neutral5 : 'transparent',
  } as CSSProperties;

  return (
    <section ref={containerRef} {...referenceProps} css={containerStyle} style={gridStyle}>
      <TopCornerCells onScroll={handleScroll} navigationConfig={navigationConfig} />
      <TimeColumn />
      <ColumnGroupSections />
      <DragCreateSelectionOverlay isEnabled={isAllowDragCreateSelection} />
      {NoEventsComponent && (
        <div css={noEventsOverlayStyle}>
          <NoEventsComponent maxWidth='calc(var(--viewport-width) - var(--time-column-width))' />
        </div>
      )}
      <CalendarTooltip
        hoverMinutes={tooltipInfo.hoverMinutes}
        hoveredEventId={tooltipInfo.hoveredEventId}
        containerRef={tooltipInfo.refs.setFloating}
        floatingStyles={tooltipInfo.floatingStyles}
        getFloatingProps={tooltipInfo.getFloatingProps}
      />
    </section>
  );
};

type CalendarGridProps = CalendarGridProviderProps & Omit<CalendarGridComponentProps, 'hideVerticalOverflow'>;

export const CalendarGrid = ({ NoEventsComponent, navigationConfig, onEventCreate, ...props }: CalendarGridProps) => {
  return (
    <CalendarGridProvider {...props}>
      <CalendarGridComponent
        NoEventsComponent={NoEventsComponent}
        hideVerticalOverflow={!!NoEventsComponent}
        navigationConfig={navigationConfig}
        onEventCreate={onEventCreate}
      />
    </CalendarGridProvider>
  );
};

const containerStyle = css({
  '--time-column-width': '40px',
  '--header-height': 'max-content',
  '--group-separator-width': '40px',
  '--total-mins': TOTAL_HOURS * 60,
  '--total-hours': TOTAL_HOURS,
  height: '100%',
  display: 'grid',
  position: 'relative',
  /* Critical for performance: layout isolation */
  contain: 'layout size',
  overflow: 'auto',

  // snap scroll styles
  scrollSnapType: 'x mandatory',
  scrollBehavior: 'smooth',
  scrollPaddingLeft: 'var(--time-column-width)',
});

const noEventsOverlayStyle = css({
  position: 'sticky',
  left: 'var(--time-column-width)',
  zIndex: CALENDAR_Z_INDICES.timeColumn - 1,
  width: 'calc(var(--viewport-width) - var(--time-column-width))',
  height: 'calc(var(--viewport-height) - 70px)', // 70px is an arbitrary value to account for the header height, can be adjusted as needed
  gridRow: '2 / -1',
  gridColumn: '2 / -1',
  justifySelf: 'start',
  alignSelf: 'start',
});
